/**
 * Repair, which is NOT Retry — and the button on the job row used to be both.
 *
 * WHAT WAS THERE. A "Retry" button on every FAILED provisioning job, posting to
 * `POST /provisioning/jobs/{job_id}/retry`. That route re-runs the SAME row and
 * never grants a fresh retry budget. On a row that reached FAILED by consuming
 * its budget (`retry_count >= max_retries`) it bought exactly one attempt whose
 * first failure is terminal immediately — no backoff ladder — while presenting
 * itself to the operator as a fresh lifecycle. The backend now answers that
 * case with 409 naming Repair, and the operator-facing action is Repair:
 *
 *     GET  /api/admin/tenants/{id}/provisioning/repair-preview   read-only
 *     POST /api/admin/tenants/{id}/provisioning/repair           202
 *
 * Repair mints a NEW job row with a fresh budget, reusing the frozen spec's
 * `provisioning_request_id` so the platform converges on the same claim.
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO
 *
 * 1. It does not call itself Retry, and it does not sit on the failed row as
 *    though it continued that attempt. `retry_count` is monotonic per row: the
 *    failed row's history stays terminal and the new row's count starts at
 *    zero. A control that implied otherwise is the misreading this change
 *    exists to remove.
 * 2. It does not present the preview's numbers as a promise. `measured: false`
 *    means the verification could not be read, and the panel says so instead of
 *    rendering `0 missing` — a zero there states a measurement nobody made.
 *
 * PREVIEW BEFORE COMMIT, INLINE. `repair-preview` is read-only on both sides,
 * so an operator can look before pressing. Rendered as a disclosure on the page
 * rather than a modal: this is a thing you read next to the job history that
 * prompted it, and a dialog takes that away.
 */
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Loader2, OctagonAlert, Wrench } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { describeStage } from './provisioningStages';

/** The detail body of a FastAPI error, which may be a string or an object. */
export function repairErrorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.reason || fallback;
  }
  return fallback;
}

/**
 * The preview as words. Pure.
 *
 * `measured` is the load-bearing field. When the platform verification could
 * not be read there is no missing-step count, and `missing_count: 0` under
 * `measured: false` must never render as "nothing is missing".
 */
export function describeRepairPreview(preview) {
  if (!preview) return null;
  const measured = preview.measured === true;
  return {
    measured,
    available: preview.repair_available === true,
    blockedReason: preview.blocked_reason || null,
    lifecycle: preview.lifecycle_state || null,
    // NULL WHEN UNMEASURED, never 0.
    missingCount: measured && Number.isFinite(preview.missing_count)
      ? String(preview.missing_count) : null,
    healthyCount: measured && Number.isFinite(preview.healthy_count)
      ? String(preview.healthy_count) : null,
    missing: measured && Array.isArray(preview.missing) ? preview.missing : [],
    verificationError: preview.verification_error || null,
    // The tenant's OWN approved choice. `null` means no frozen spec was found,
    // which is a different fact from "sample data was not bought".
    sampleDataRequested: preview.sample_data_requested === true ? true
      : (preview.sample_data_requested === false ? false : null),
    specId: preview.spec_id || null,
    preserved: Array.isArray(preview.preserved) ? preview.preserved : [],
  };
}

export function RepairProvisioningPanel({
  open, loading, error, preview, submitting, onOpen, onCancel, onConfirm,
}) {
  const view = describeRepairPreview(preview);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={onOpen} data-testid="repair-provisioning-open">
        {/* An ACTION: flat, monochrome, one stroke weight. Never artwork. */}
        <Wrench className="mr-2 h-4 w-4" aria-hidden="true" />
        Repair provisioning
      </Button>
    );
  }

  return (
    <section
      className="rounded-xl border border-border bg-muted/20 p-4"
      data-testid="repair-provisioning-panel"
    >
      <div className="flex items-start gap-3">
        {loading
          ? <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
          : <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />}
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground">Repair provisioning</h4>
          {/* THE DISTINCTION, STATED. A re-run of a terminal row and a new
              provisioning job are not the same operation, and the whole reason
              this control changed is that the old one hid the difference. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Starts a <span className="font-medium text-foreground">new provisioning job</span> against
            the tenant&apos;s frozen specification, with a fresh retry budget. It does not resume the
            failed job above — that attempt stays terminal, and its retry count is not carried over.
          </p>
        </div>
      </div>

      {loading && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="repair-preview-loading">
          Reading what repair would reconcile…
        </p>
      )}

      {!loading && error && (
        // A FAILED PREVIEW IS NOT A CLEAN ONE. The operator is told the check
        // did not happen, rather than shown a panel with nothing in it.
        <div
          className="mt-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2"
          data-testid="repair-preview-error"
        >
          <p className="flex items-start gap-2 text-xs text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" aria-hidden="true" />
            <span>
              <span className="font-medium">The preview could not be read.</span>{' '}
              {error} Nothing below was measured, and repair has not been started.
            </span>
          </p>
        </div>
      )}

      {!loading && !error && view && (
        <div className="mt-3 space-y-3" data-testid="repair-preview">
          {!view.available && view.blockedReason && (
            <p
              className="flex items-start gap-2 rounded-lg border border-destructive-text/40 bg-destructive/5 px-3 py-2 text-xs text-foreground"
              data-testid="repair-preview-blocked"
              role="alert"
            >
              <OctagonAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive-text" aria-hidden="true" />
              <span><span className="font-medium">Repair cannot run: </span>{view.blockedReason}</span>
            </p>
          )}

          {view.measured ? (
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-muted-foreground">Steps missing</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground" data-testid="repair-missing-count">
                  {view.missingCount}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Steps complete</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground" data-testid="repair-healthy-count">
                  {view.healthyCount}
                </dd>
              </div>
            </dl>
          ) : (
            // NOT `0 missing`. A zero here states a measurement nobody made.
            <p className="text-xs text-muted-foreground" data-testid="repair-preview-unmeasured">
              The platform verification could not be read
              {view.verificationError ? ` — ${view.verificationError}` : ''}. How much is missing
              is unknown, not none. Repair can still be started, and this screen cannot say what
              it will find.
            </p>
          )}

          {view.measured && view.missing.length > 0 && (
            <ul className="space-y-1" data-testid="repair-missing-list">
              {view.missing.map((step) => (
                <li key={String(step?.step ?? step)} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-text" aria-hidden="true" />
                  {/* Named, then identified — through the same mapping the
                      progress sentence and the step table use, so the operator
                      reading "Page layouts" here and "Page layouts" there is
                      reading one vocabulary. The slug follows in mono because
                      this list is what gets quoted into a bug report. */}
                  <span className="break-words">
                    {describeStage(step?.step ?? step)?.label ?? String(step?.step ?? step)}
                    <span className="ml-1.5 font-mono text-[11px] opacity-70">
                      {String(step?.step ?? step)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {view.measured && view.missing.length === 0 && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
              Every verification step the platform reported is complete.
            </p>
          )}

          <p className="text-xs text-muted-foreground" data-testid="repair-sample-data">
            Sample records:{' '}
            {view.sampleDataRequested === null
              ? 'not known — no frozen specification was found for this tenant'
              : view.sampleDataRequested
                ? 'this tenant approved them, so repair will reconcile them'
                : 'this tenant did not approve them, and repair will not create any'}
            .
          </p>

          {view.preserved.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Preserved and never rewritten: {view.preserved.join(', ')}.
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={submitting || loading || (view ? !view.available : false)}
          data-testid="repair-provisioning-confirm"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          Start repair
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="repair-provisioning-cancel">
          Cancel
        </Button>
      </div>
    </section>
  );
}

/** The fetching half. */
export default function RepairProvisioningAction({ tenantId, api, headers, onStarted, notify }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openPanel = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const { data } = await axios.get(
        `${api}/tenants/${tenantId}/provisioning/repair-preview`, { headers });
      setPreview(data);
    } catch (err) {
      // A failed preview is kept apart from an empty one all the way to the
      // render, so it can never read as "nothing needs repairing".
      setError(repairErrorText(err, 'The request did not complete.'));
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, headers]);

  const confirm = useCallback(async () => {
    setSubmitting(true);
    try {
      await axios.post(`${api}/tenants/${tenantId}/provisioning/repair`, {}, { headers });
      notify?.success('Repair started — a new provisioning job is running in the background.');
      setOpen(false);
      onStarted?.();
    } catch (err) {
      // 409 IS AN ORDINARY ANSWER HERE — a job already running, no frozen spec,
      // or a terminal row whose detail names Repair as the right operation. Its
      // text is surfaced rather than flattened into a generic failure, and it
      // is NEVER retried automatically.
      notify?.error(repairErrorText(err, 'The repair could not be started.'));
    } finally {
      setSubmitting(false);
    }
  }, [api, tenantId, headers, notify, onStarted]);

  return (
    <RepairProvisioningPanel
      open={open}
      loading={loading}
      error={error}
      preview={preview}
      submitting={submitting}
      onOpen={openPanel}
      onCancel={() => setOpen(false)}
      onConfirm={confirm}
    />
  );
}
